import {
  createHttpError,
  withRefreshedAccessTokenRetry,
} from "./googleAuthSession.js";
import {
  DEFAULT_ENABLE_FUZZ,
  DEFAULT_ENABLE_SHORT_TERM,
  DEFAULT_FSRS_PARAMETERS,
  DEFAULT_LEARNING_STEPS_MINUTES,
  DEFAULT_PLANNER_TARGET,
  DEFAULT_RELEARNING_STEPS_MINUTES,
  normalizeFsrsParameters,
  normalizeStepMinutes,
  normalizePlannerTarget,
  normalizeSchedulerModel,
} from "../model/fsrsScheduler.js";

const DEFAULT_PARAMS = {
  showBothSides: true,
  useRandomOrder: true,
  model: "FSRS",
  plannerTarget: DEFAULT_PLANNER_TARGET,
  fsrsFuzz: DEFAULT_ENABLE_FUZZ,
  fsrsDelaySiblings: true,
  fsrsEnableShortTerm: DEFAULT_ENABLE_SHORT_TERM,
  fsrsLearningStepsMinutes: DEFAULT_LEARNING_STEPS_MINUTES,
  fsrsRelearningStepsMinutes: DEFAULT_RELEARNING_STEPS_MINUTES,
  fsrsParameters: DEFAULT_FSRS_PARAMETERS,
  fsrsUpdatedAt: "",
  fsrsUpdateSize: 0,
  noButtonText: "No",
  yesButtonText: "Yes",
};

const PARAM_ROWS = [
  ["Show both sides", "showBothSides", "boolean"],
  ["Use random order", "useRandomOrder", "boolean"],
  ["Model (FSRS/ML)", "model", "model"],
  ["Planner target", "plannerTarget", "number"],
  ["FSRS fuzz", "fsrsFuzz", "boolean"],
  ["Delay siblings", "fsrsDelaySiblings", "boolean"],
  ["FSRS enable_short_term", "fsrsEnableShortTerm", "boolean"],
  ["FSRS learning steps (minutes)", "fsrsLearningStepsMinutes", "number_array_steps"],
  ["FSRS relearning steps (minutes)", "fsrsRelearningStepsMinutes", "number_array_steps"],
  ["FSRS parameters", "fsrsParameters", "number_array"],
  ["FSRS updated at", "fsrsUpdatedAt", "string"],
  ["FSRS update size", "fsrsUpdateSize", "raw_number"],
  ["❌ button text", "noButtonText", "string"],
  ["✅ button text", "yesButtonText", "string"],
];

const EMOJI_SUBSTITUTIONS = [
  [/:red x emoji:/gi, "❌"],
  [/:green check emoji:/gi, "✅"],
];

const parseBooleanCell = (value, fallback) => {
  if (typeof value === "boolean") {
    return value;
  }

  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (normalized === "true") {
      return true;
    }
    if (normalized === "false") {
      return false;
    }
  }

  return fallback;
};

const parseStringCell = (value, fallback) => {
  if (value == null) {
    return fallback;
  }

  const normalized = EMOJI_SUBSTITUTIONS.reduce(
    (nextValue, [pattern, replacement]) =>
      String(nextValue).replace(pattern, replacement),
    String(value)
  ).trim();

  return normalized || fallback;
};

const parseNumberCell = (value, fallback) => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : fallback;
};

export const buildParamsSheetValues = (params = DEFAULT_PARAMS) =>
  PARAM_ROWS.map(([label, key, type]) => {
    if (type === "boolean") {
      return [label, params[key] ? true : false];
    }
    if (type === "string") {
      return [label, parseStringCell(params[key], DEFAULT_PARAMS[key])];
    }
    if (type === "model") {
      return [label, normalizeSchedulerModel(params[key])];
    }
    if (type === "number") {
      return [label, normalizePlannerTarget(params[key])];
    }
    if (type === "raw_number") {
      return [label, parseNumberCell(params[key], DEFAULT_PARAMS[key])];
    }
    if (type === "number_array") {
      return [label, ...normalizeFsrsParameters(params[key])];
    }
    if (type === "number_array_steps") {
      return [
        label,
        ...normalizeStepMinutes(params[key], DEFAULT_PARAMS[key]),
      ];
    }
    return [label];
  });

export const parseParamsSheet = (sheet) => {
  const values = Array.isArray(sheet?.values) ? sheet.values : [];
  const parsed = { ...DEFAULT_PARAMS };

  PARAM_ROWS.forEach(([label, key, type]) => {
    const matchingRow = values.find(
      (row) => String(row?.[0] ?? "").trim().toLowerCase() === label.toLowerCase()
    );
    if (matchingRow) {
      if (type === "boolean") {
        parsed[key] = parseBooleanCell(matchingRow[1], parsed[key]);
      } else if (type === "string") {
        parsed[key] = parseStringCell(matchingRow[1], parsed[key]);
      } else if (type === "model") {
        parsed[key] = normalizeSchedulerModel(matchingRow[1]);
      } else if (type === "number") {
        parsed[key] = normalizePlannerTarget(
          parseNumberCell(matchingRow[1], parsed[key])
        );
      } else if (type === "raw_number") {
        parsed[key] = parseNumberCell(matchingRow[1], parsed[key]);
      } else if (type === "number_array") {
        parsed[key] = normalizeFsrsParameters(matchingRow.slice(1));
      } else if (type === "number_array_steps") {
        parsed[key] = normalizeStepMinutes(
          matchingRow.slice(1),
          DEFAULT_PARAMS[key]
        );
      }
    }
  });

  return parsed;
};

const getColumnLabel = (columnIndex) => {
  let index = Math.max(Number(columnIndex), 1);
  let label = "";
  while (index > 0) {
    const remainder = (index - 1) % 26;
    label = String.fromCharCode(65 + remainder) + label;
    index = Math.floor((index - 1) / 26);
  }
  return label;
};

export const writeParamsSheet = (
  spreadsheetAccessData,
  params,
  sheetTitle = "params"
) => {
  const values = buildParamsSheetValues(params);
  const maxColumns = values.reduce(
    (largestCount, row) => Math.max(largestCount, row.length),
    2
  );
  const range = `${sheetTitle}!A1:${getColumnLabel(maxColumns)}${values.length}`;
  return withRefreshedAccessTokenRetry(
    spreadsheetAccessData,
    async (nextAccessData) => {
      const response = await fetch(
        `https://sheets.googleapis.com/v4/spreadsheets/${nextAccessData.spreadsheetId}/values/${encodeURIComponent(
          range
        )}?valueInputOption=USER_ENTERED`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${nextAccessData.access_token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            range,
            majorDimension: "ROWS",
            values,
          }),
        }
      );

      if (!response.ok) {
        const body = await response.json().catch(() => null);
        throw createHttpError(
          `Failed to write params sheet: ${response.status}`,
          response.status,
          body
        );
      }

      return response.json();
    },
    {
      operationName: "Params sheet write",
    }
  );
};

export { DEFAULT_PARAMS };
